import React, { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let M = "/module-rbac/hris";

const RoleDialog = ({ open, role, onClose, onSaved }) => {
  const [f, setF] = useState({ name: "", description: "" });
  useEffect(() => { setF(role ? { name: role.name, description: role.description || "" } : { name: "", description: "" }); }, [role, open]);
  const save = async () => {
    try {
      const { data } = role ? await api.put(`${M}/roles/${role.id}`, { ...f, permissions: role.permissions }) : await api.post(`${M}/roles`, { ...f, permissions: [] });
      toast.success(role ? "Role updated" : "Role created \u2014 now tick its permissions"); onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{role ? "Edit role" : "Create role"}</DialogTitle><DialogDescription>Roles here only apply to this module.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Role name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. HRIS Admin, Payroll Officer" data-testid="hris-role-name" /></div>
          <div><Label className="text-xs">Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="hris-role-desc" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name.trim()} data-testid="hris-role-save">{role ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Group = ({ g, draft, toggle, toggleGroup, locked }) => {
  const keys = g.items.map((i) => i.key);
  const on = keys.filter((k) => draft.has(k)).length;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`hris-perm-group-${g.key}`}>
      <label className="flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-100 bg-slate-50/70 cursor-pointer">
        <Checkbox checked={on === keys.length ? true : on ? "indeterminate" : false} disabled={locked} onCheckedChange={(c) => toggleGroup(keys, !!c)} data-testid={`hris-perm-group-toggle-${g.key}`} />
        <span className="font-semibold text-sm text-slate-800">{g.group}</span>
        <span className="text-[11px] text-slate-500">Sidebar navigation · {on}/{keys.length}</span>
      </label>
      <div className="grid sm:grid-cols-2 gap-x-4">
        {g.items.map((i) => (
          <label key={i.key} className="flex items-start gap-2.5 px-4 py-2 cursor-pointer hover:bg-slate-50">
            <Checkbox className="mt-0.5" checked={draft.has(i.key)} disabled={locked} onCheckedChange={() => toggle(i.key)} data-testid={`hris-perm-${i.key}`} />
            <span className="min-w-0"><span className="text-sm text-slate-800">{i.label}</span>{i.description && <span className="block text-[11px] text-slate-500">{i.description}</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default function HrisRoles({ mid = "hris", title = "HRIS Roles" }) {
  M = `/module-rbac/${mid}`;
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [dlg, setDlg] = useState({ open: false, role: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (pickId) => {
    const { data } = await api.get(`${M}/roles`);
    setRoles(data);
    const r = data.find((x) => x.id === pickId) || data.find((x) => x.id === sel?.id) || data.find((x) => !x.builtin) || data[0];
    setSel(r); setDraft(new Set(r?.permissions || []));
  }, [sel]);
  useEffect(() => { api.get(`${M}/catalog`).then(({ data }) => setCatalog(data.groups)); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (r) => { setSel(r); setDraft(new Set(r.permissions || [])); };
  const toggle = (k) => setDraft((d) => { const n = new Set(d); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleGroup = (keys, on) => setDraft((d) => { const n = new Set(d); keys.forEach((k) => (on ? n.add(k) : n.delete(k))); return n; });
  const savePerms = async () => {
    setSaving(true);
    try { await api.put(`${M}/roles/${sel.id}`, { name: sel.name, description: sel.description || "", permissions: [...draft] }); toast.success("Permissions saved"); await load(sel.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`${M}/roles/${r.id}`); toast.success("Role deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const locked = !sel || sel.builtin;
  const dirty = sel && JSON.stringify([...draft].sort()) !== JSON.stringify([...(sel.permissions || [])].sort());

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden h-fit" data-testid="hris-roles-list">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-semibold text-sm">{title}</div>
          <Button size="sm" onClick={() => setDlg({ open: true, role: null })} data-testid="hris-role-add"><Icons.Plus className="h-4 w-4 mr-1" />New Role</Button>
        </div>
        {roles.map((r) => (
          <div key={r.id} onClick={() => pick(r)} className={`flex items-center gap-2 px-4 py-3 border-b border-slate-50 cursor-pointer ${sel?.id === r.id ? "bg-blue-50/70 border-l-2 border-l-blue-600" : "hover:bg-slate-50"}`} data-testid={`hris-role-item-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">{r.builtin && <Icons.Lock className="h-3 w-3 text-slate-400" />}{r.name}</div>
              <div className="text-[11px] text-slate-500">{r.builtin ? "Default self-service" : `${(r.permissions || []).length} permission(s)`} · {r.user_count} user(s)</div>
            </div>
            {!r.builtin && <>
              <button onClick={(e) => { e.stopPropagation(); setDlg({ open: true, role: r }); }} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`hris-role-edit-${r.id}`}><Icons.Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`hris-role-delete-${r.id}`}><Icons.Trash2 className="h-3.5 w-3.5" /></button>
            </>}
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div><div className="font-semibold text-slate-900" data-testid="hris-perm-role-name">{sel?.name || "Pick a role"}</div>
            <div className="text-xs text-slate-500">{locked ? "The Employee role is fixed: Clock In/Out, Forms and own records only." : "Ticking a group shows it in the sidebar; untick to hide and secure it."}</div></div>
          {!locked && <Button onClick={savePerms} disabled={!dirty || saving} data-testid="hris-perm-save">{saving ? <Icons.Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icons.Save className="h-4 w-4 mr-1.5" />}Save Permissions</Button>}
        </div>
        {catalog.map((g) => <Group key={g.key} g={g} draft={draft} toggle={toggle} toggleGroup={toggleGroup} locked={locked} />)}
      </div>
      <RoleDialog open={dlg.open} role={dlg.role} onClose={() => setDlg({ open: false, role: null })} onSaved={(r) => { setDlg({ open: false, role: null }); load(r.id); }} />
    </div>
  );
}
